import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,

  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LazyCartDrawer } from "@/components/LazyCartDrawer";
import { QuickOrderDialog } from "@/components/QuickOrderDialog";

import { ShopProvider } from "@/lib/shop-store";
import { installChunkErrorLogging } from "@/lib/chunk-error-log";
import { useBuildVersionCheck } from "@/lib/use-build-version";
import { installClientErrorLogging, reportClientError } from "@/lib/client-error-log";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    reportClientError(error, "react_error_boundary");
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Sunset Concept Store — Beachwear & Resort" },
      {
        name: "description",
        content:
          "Sunset Concept Store, concept store féminin méditerranéen : beachwear, kimonos, sacs et accessoires.",
      },
      { name: "author", content: "Sunset Concept Store" },
      { property: "og:title", content: "Sunset Concept Store — Beachwear & Resort" },
      {
        property: "og:description",
        content: "Beachwear, kimonos, sacs et accessoires. Livraison partout au Maroc.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Sunset Concept Store" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      // Polices auto-hébergées : preload des woff2 critiques (latin, variables)
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/jost-latin-wght-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/cormorant-garamond-latin-wght-normal.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.sunsetconcept.store/#organization",
              name: "Sunset Concept Store",
              url: "https://www.sunsetconcept.store/",
              logo: {
                "@type": "ImageObject",
                url: "https://www.sunsetconcept.store/logo-sunset.png",
                width: 600,
                height: 178,
              },
              image: "https://www.sunsetconcept.store/logo-sunset.png",
              description:
                "Concept store féminin méditerranéen : beachwear, kimonos, sacs et accessoires. Livraison partout au Maroc.",
              areaServed: "MA",
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  areaServed: "MA",
                  availableLanguage: ["fr", "ar"],
                  url: "https://www.sunsetconcept.store/contact",
                },
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://www.sunsetconcept.store/#website",
              name: "Sunset Concept Store",
              url: "https://www.sunsetconcept.store/",
              inLanguage: "fr-MA",
              publisher: { "@id": "https://www.sunsetconcept.store/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => installChunkErrorLogging(), []);
  useEffect(() => installClientErrorLogging(), []);
  useBuildVersionCheck();



  return (
    <QueryClientProvider client={queryClient}>
      <ShopProvider>
        <Header />
        <main className="min-h-screen overflow-x-hidden">
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <Footer />
        <LazyCartDrawer />
        <QuickOrderDialog />

      </ShopProvider>
    </QueryClientProvider>
  );
}


